import { useEffect, useState } from 'react';
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { ArrowLeft, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ROUTES } from '@/utilities/constant';
import {
  API_VERIFY_EMAIL,
  API_RESEND_EMAIL_VERIFICATION,
} from '@/service/url';
import { postApiNoAuthCall } from '@/service/postApiCall';

/*
 * VerifyEmailPage — port of views/dashboard/auth/verify-email.js.
 *
 * Logic preserved byte-identical:
 *   • Reads username from `location.state.username` or `?username=` search param on mount
 *   • Verify: POST API_VERIFY_EMAIL with { username, otp }; on success show toast + navigate to LOGIN after 1.2s
 *   • Resend: POST API_RESEND_EMAIL_VERIFICATION with { username }; toast success + optional "Sent to <email>"
 *   • Same validation: username required; OTP must be 6 digits
 *   • OTP input strips non-digits and truncates to 6 chars
 */
export function VerifyEmailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [username, setUsername] = useState('');
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);

  useEffect(() => {
    const fromState = (location.state as { username?: string } | null)
      ?.username;
    const fromQuery = searchParams.get('username');
    const initial = (fromState || fromQuery || '').trim();
    if (initial) setUsername(initial);
  }, [location.state, searchParams]);

  const submitVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) {
      toast.error('Username is required');
      return;
    }
    if (!otp.trim() || otp.trim().length !== 6) {
      toast.error('Please enter the 6-digit code from your email');
      return;
    }
    setBusy(true);
    try {
      const resp = await postApiNoAuthCall(API_VERIFY_EMAIL, {
        username: username.trim(),
        otp: otp.trim(),
      });
      if ((resp as { success?: boolean })?.success) {
        toast.success(
          (resp as { message?: string })?.message ||
            'Email verified. You can sign in now.',
        );
        setTimeout(() => navigate(`/${ROUTES.LOGIN}`), 1200);
      }
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Verification failed');
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (!username.trim()) {
      toast.error('Enter your username first');
      return;
    }
    setResendBusy(true);
    try {
      const resp = await postApiNoAuthCall(API_RESEND_EMAIL_VERIFICATION, {
        username: username.trim(),
      });
      const r = resp as {
        success?: boolean;
        message?: string;
        data?: { email?: string };
      };
      if (r?.success) {
        toast.success(r.message || 'A new code has been sent to your email');
        if (r.data?.email) {
          toast.info(`Sent to ${r.data.email}`);
        }
      }
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Could not resend code');
    } finally {
      setResendBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 py-10">
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-8 shadow-card">
        <div className="mb-6 flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Mail className="h-4 w-4" />
          </div>
          <div>
            <h1 className="text-lg font-semibold tracking-tight text-foreground">
              Verify your email
            </h1>
            <p className="text-[12px] text-muted-foreground">
              Enter the 6-digit code sent to your email. New accounts must
              verify before signing in.
            </p>
          </div>
        </div>

        <form onSubmit={submitVerify} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="ve-username" className="text-xs">
              Username
            </Label>
            <Input
              id="ve-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Your username"
              autoComplete="username"
              className="h-10"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ve-otp" className="text-xs">
              Verification code
            </Label>
            <Input
              id="ve-otp"
              value={otp}
              onChange={(e) =>
                setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))
              }
              placeholder="6-digit code"
              inputMode="numeric"
              maxLength={6}
              className="h-10 font-mono tabular-nums tracking-[0.4em]"
            />
          </div>

          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={busy}
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Verify email'
            )}
          </Button>

          <Button
            type="button"
            size="lg"
            variant="outline"
            className="w-full"
            disabled={resendBusy}
            onClick={resend}
          >
            {resendBusy ? 'Sending…' : 'Resend code'}
          </Button>
        </form>

        <div className="mt-6 text-center text-[13px]">
          <Link
            to={`/${ROUTES.LOGIN}`}
            className="inline-flex items-center gap-1 text-primary hover:underline"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
